import { useCallback, useMemo, useState } from 'react';
import {
  SentenceSetFromApi,
  SetType,
  TextSetFromApi,
  WordSetFromApi,
} from '@/stores/store.types';
import { SCREEN_NAME_ACCORD_TYPE } from '@/views/SetCard/constants';

type SetTypeFilterValue = 'WordSet' | 'SentenceSet' | 'TextSet';

export type CombinedSet = {
  data: WordSetFromApi | SentenceSetFromApi | TextSetFromApi;
  type: SetType;
};

export const useSetCardFilter = (
  combinedSets: CombinedSet[],
  onFilterCommit?: () => void,
) => {
  const [typeFilter, setTypeFilter] = useState<SetTypeFilterValue[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [appliedTypeFilter, setAppliedTypeFilter] = useState<
    SetTypeFilterValue[]
  >([]);
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');

  const countFilterApplied = useMemo(() => {
    let count = 0;
    if (appliedTypeFilter.length > 0) {
      count += appliedTypeFilter.length;
    }
    if (appliedSearchTerm.trim() !== '') {
      count += 1;
    }
    return count;
  }, [appliedTypeFilter, appliedSearchTerm]);

  const handleSetTypeFilter = useCallback(
    (value: SetTypeFilterValue, isSelected: boolean) => {
      if (isSelected) {
        setTypeFilter((state) => state.concat(value));
      } else {
        setTypeFilter((state) => state.filter((type) => type !== value));
      }
    },
    [],
  );

  const applyFilter = useCallback(() => {
    setAppliedTypeFilter(typeFilter);
    setAppliedSearchTerm(searchTerm);
    onFilterCommit?.();
  }, [typeFilter, searchTerm, onFilterCommit]);

  const resetFilter = useCallback(() => {
    setTypeFilter([]);
    setSearchTerm('');
    setAppliedTypeFilter([]);
    setAppliedSearchTerm('');
    onFilterCommit?.();
  }, [onFilterCommit]);

  const filteredSets = useMemo(() => {
    return combinedSets.filter(({ type, data }) => {
      const typeMatches =
        appliedTypeFilter.length === 0 ||
        appliedTypeFilter.includes(SCREEN_NAME_ACCORD_TYPE[type]);

      const searchTermMatches = data.name
        .toLowerCase()
        .includes(appliedSearchTerm.toLowerCase());

      return typeMatches && searchTermMatches;
    });
  }, [combinedSets, appliedTypeFilter, appliedSearchTerm]);

  return {
    filteredSets,
    countFilterApplied,
    filterProps: {
      typeFilter,
      setTypeFilter: handleSetTypeFilter,
      searchTerm,
      setSearchTerm,
      applyFilter,
      resetFilter,
    },
  };
};
